/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useEffect, useMemo, useRef, useState } from "react"

import type { GPTModelItem } from "@/api/models"
import type { ReasoningEffortOverride, ReasoningMode } from "@/stores/ai-explorer-store"
import type { SessionType } from "@/types"

import { EFFORT_LEVELS } from "@/api/admin/models"
import {
  DEFAULT_REASONING_PRESETS,
  normalizeReasoningPresets,
  resolveReasoningPresetLocks,
} from "@/features/chat/utils/reasoning-presets"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useGPTModelSettings } from "@/hooks/use-gpt-model-settings"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"

// Re-exported for existing consumers (the request builders' complexity
// classification, and this file's own test-mock harnesses) — the
// classification logic itself lives in a dependency-free module so it can be
// imported without pulling in this hook's React/auth/config-query chain (see
// the browser-fixture "auth setup" regression on issue #2893).
export { classifyAutoEffort } from "@/features/chat/utils/auto-effort-classifier"

type UseHighReasoningProps = {
  files?: File[]
  handleRemoveFile?: (file: File) => void
  sessionType: SessionType
  /** The active project's configured default model id, if any. Takes over
   *  from the raw persisted `gptModel` setting when no override is active —
   *  see the Auto-fallback note on `effectiveModel` below. */
  projectDefaultModelId?: string | null
  /** The current route's preselected model and effort (`defaultModelId` /
   *  `defaultReasoningEffort`, #3327). Used when the user has made no pick
   *  and the active project sets no default model. */
  routeDefaultModelId?: string | null
  routeDefaultEffort?: string | null
}

/**
 * `model` with `default_effort` replaced by `effort`, snapped down to the
 * highest stop the model offers this user at or below it. The model's own
 * default is kept when `effort` is unknown or below every stop.
 */
function withDefaultEffort(model: GPTModelItem, effort: string | null | undefined): GPTModelItem {
  const target = EFFORT_LEVELS.indexOf(effort as (typeof EFFORT_LEVELS)[number])
  if (target < 0) return model
  const supported = model.supported_efforts ?? []
  const snapped = [...supported]
    .filter((e) => {
      const index = EFFORT_LEVELS.indexOf(e as (typeof EFFORT_LEVELS)[number])
      return index >= 0 && index <= target
    })
    .sort(
      (a, b) =>
        EFFORT_LEVELS.indexOf(b as (typeof EFFORT_LEVELS)[number]) -
        EFFORT_LEVELS.indexOf(a as (typeof EFFORT_LEVELS)[number])
    )[0]
  return snapped ? { ...model, default_effort: snapped } : model
}

/**
 * Last-resort fallback when a preset has no effort lock AND the selected
 * model's own (already access-clamped) `default_effort` isn't available yet
 * — e.g. before the model catalog has loaded. Should rarely be hit: named
 * presets normally carry a concrete effort, and once `effectiveModel` is
 * known its `default_effort` wins instead (see `reasoningEffort` below).
 */
const MODE_TO_EFFORT: Record<ReasoningMode, ReasoningEffortOverride> = {
  auto: "medium", // classifyAutoEffort overrides at request time
  fast: DEFAULT_REASONING_PRESETS.fast.effort ?? "medium",
  thinking: DEFAULT_REASONING_PRESETS.thinking.effort ?? "high",
  big_brain: DEFAULT_REASONING_PRESETS.big_brain.effort ?? "max",
}

export function useHighReasoning(props: UseHighReasoningProps) {
  const {
    files = [],
    handleRemoveFile,
    sessionType,
    projectDefaultModelId,
    routeDefaultModelId,
    routeDefaultEffort,
  } = props
  const { availableModels } = useGPTModelSettings(sessionType)
  const explorerStore = useAIExplorerStore(sessionType)
  const reasoningMode = explorerStore((state) => state.reasoningMode)
  const setReasoningMode = explorerStore((state) => state.setReasoningMode)
  const modelOverride = explorerStore((state) => state.modelOverride)
  const setModelOverride = explorerStore((state) => state.setModelOverride)
  const effortOverride = explorerStore((state) => state.effortOverride)
  const setEffortOverride = explorerStore((state) => state.setEffortOverride)

  const { data: reasoningPresets } = useConfigQuery({
    select: (config) => normalizeReasoningPresets(config.reasoning_presets),
  })
  const presets = reasoningPresets ?? DEFAULT_REASONING_PRESETS
  const { data: catalogModels } = useConfigQuery({
    select: (config) => config.models,
  })

  const availableModelIds = useMemo(
    () => new Set(availableModels.map((model) => model.id)),
    [availableModels]
  )

  // Derived: Big Brain (or any mode whose effective model is a reasoning model)
  // drives the image-upload warning. Treat reasoning_effort="high" as
  // backward-compatible semantics for existing consumers.
  const enableHighReasoning = reasoningMode === "big_brain"

  const [showImageReasoningWarning, setShowImageReasoningWarning] = useState(false)

  // Resolve the model that will actually be used: advanced/preset override →
  // the active project's configured model (if any) → the route's default
  // model (if any) → Auto. Deliberately
  // ignores the raw persisted `gptModel` setting as the generic fallback —
  // nothing writes it as a personal default anymore (the header picker that
  // did was removed), so trusting it here would silently run a stale model
  // the user has no way to change. This must stay the single source of truth
  // for "what model actually runs" — it feeds both the UI (selector, token
  // limit, image gating) and the request payload via getEffectiveModelId.
  const autoModel = availableModels.find((m) => m.id === "auto")
  const projectDefaultModel = projectDefaultModelId
    ? availableModels.find((m) => m.id === projectDefaultModelId)
    : undefined
  // Absence from the session-filtered list is not "the user cannot use it":
  // a group-gated model this user may use is dropped there whenever the route
  // id is not that group (#3314). Resolve against the config payload. An id
  // that payload does not contain is unusable — fall back, and do not invent
  // a stub. The route's effort rides along as the model's default effort, so
  // the pill, `reasoningEffort` and the send path all pick it up.
  const routeDefaultModel = useMemo(() => {
    const model = routeDefaultModelId
      ? catalogModels?.find((candidate) => candidate.id === routeDefaultModelId)
      : undefined
    return model ? withDefaultEffort(model, routeDefaultEffort) : undefined
  }, [catalogModels, routeDefaultModelId, routeDefaultEffort])
  // The picker offers the session list. A route default already in that list
  // is replaced by the overlaid model so re-selecting it keeps the snapped
  // route effort; a default the session filter dropped is appended. The id
  // is never listed twice.
  const selectableModels = useMemo(() => {
    if (!routeDefaultModel) return availableModels
    const index = availableModels.findIndex((model) => model.id === routeDefaultModel.id)
    if (index < 0) return [...availableModels, routeDefaultModel]
    if (availableModels[index] === routeDefaultModel) return availableModels
    const next = availableModels.slice()
    next[index] = routeDefaultModel
    return next
  }, [availableModels, routeDefaultModel])
  const effectiveModel = modelOverride
    ? (selectableModels.find((m) => m.id === modelOverride) ?? autoModel)
    : (projectDefaultModel ?? routeDefaultModel ?? autoModel)

  const isReasoningModelActive = !!effectiveModel?.feature_flags?.is_reasoning_model

  useEffect(() => {
    if (
      effectiveModel &&
      !(effectiveModel.feature_flags?.supports_images ?? true) &&
      files.some((file) => file.type.startsWith("image/"))
    ) {
      setShowImageReasoningWarning(true)
    }
  }, [effectiveModel, files])

  // True after the user adjusts model/effort in Advanced while on a slash mode.
  // Cleared on slash-mode change and on image-warning Cancel so late catalog/
  // preset loads can still fill in locks after those paths clear overrides.
  const manualOverrideRef = useRef(false)

  const handleImageReasoningWarningClose = () => {
    setShowImageReasoningWarning(false)
    if (
      effectiveModel &&
      !(effectiveModel.feature_flags?.supports_images ?? true) &&
      files.some((file) => file.type.startsWith("image/")) &&
      handleRemoveFile
    ) {
      files.forEach((file) => {
        if (file.type.startsWith("image/")) {
          handleRemoveFile(file)
        }
      })
    }
  }

  const handleImageReasoningWarningCancel = () => {
    setShowImageReasoningWarning(false)

    // Cancel keeps images and drops the incompatible model/mode. Clear the
    // Advanced sticky ref so a later catalog/preset load is not stuck skipping
    // re-resolve while mode is still /fast or /thinking with null overrides.
    manualOverrideRef.current = false
    setEffortOverride(null)
    if (reasoningMode !== "auto") {
      setReasoningMode("auto")
    }
    if (effectiveModel && (effectiveModel.feature_flags?.is_reasoning_model ?? false)) {
      // Escape via an explicit override, not a bare clear — when the
      // incompatible model came from the active project's or the route's
      // default (not from an override), clearing modelOverride to null would
      // resolve straight back to that model instead of away from it. Must also
      // filter for image support, not just "any non-auto model" — the first
      // non-auto entry can itself be the same incompatible reasoning model,
      // which would re-trigger the image-warning effect and loop forever.
      const previousModel = availableModels.find(
        (model) => model.id !== "auto" && (model.feature_flags?.supports_images ?? true)
      )
      setModelOverride(previousModel ? previousModel.id : null)
    } else {
      setModelOverride(null)
    }
  }

  const handleReasoningModeChange = (
    mode: ReasoningMode,
    // Retained for API compatibility with callers that pass a model (e.g.
    // prompt presets). Preset model/effort locks win over this arg.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    modelOverrideArg?: GPTModelItem
  ) => {
    manualOverrideRef.current = false
    setReasoningMode(mode)
    // Auto clears locks immediately so a manual model/effort pick made while
    // already in auto is not wiped by the late-config effect below.
    if (mode === "auto") {
      setModelOverride(null)
      setEffortOverride(null)
      return
    }
    const locks = resolveReasoningPresetLocks(mode, presets, availableModelIds)
    setModelOverride(locks.modelOverride)
    setEffortOverride(locks.effortOverride)
  }

  const handleModelOverrideChange = (modelId: string | null) => {
    manualOverrideRef.current = true
    setModelOverride(modelId)
  }

  const handleEffortOverrideChange = (effort: ReasoningEffortOverride | null) => {
    manualOverrideRef.current = true
    setEffortOverride(effort)
  }

  // Re-resolve slash-mode locks when admin presets or the model catalog arrive
  // after the mode was already chosen (empty catalog → effort-only; defaults →
  // admin overrides). Skip auto and skip once the user has Advanced-picked away
  // from the preset so catalog/preset refetches do not wipe those overrides.
  useEffect(() => {
    if (reasoningMode === "auto" || manualOverrideRef.current) return
    const locks = resolveReasoningPresetLocks(reasoningMode, presets, availableModelIds)
    setModelOverride(locks.modelOverride)
    setEffortOverride(locks.effortOverride)
  }, [reasoningMode, presets, availableModelIds, setModelOverride, setEffortOverride])

  // Back-compat shim for consumers that still toggle a boolean.
  const handleHighReasoningChange = (enabled: boolean, modelOverrideArg?: GPTModelItem) => {
    handleReasoningModeChange(enabled ? "big_brain" : "auto", modelOverrideArg)
  }

  const getEffectiveModelId = () => {
    // Mirrors `effectiveModel` above — must stay in lockstep so the request
    // sent to the backend always matches what the UI displayed.
    return effectiveModel?.id ?? "auto"
  }

  // Effort precedence for this UI-displayed value: slider/preset override →
  // the resolved model's own (access-clamped) default → hardcoded mode map.
  // Falling straight to MODE_TO_EFFORT on a null override would resurrect a
  // hardcoded effort even when the null came from a server-side effort-access
  // restriction (#2667) rather than genuine "no lock" — prefer the model's
  // already-safe default first.
  //
  // The request builders' send-path resolver (resolveRequestedReasoningEffort
  // in chat-request-utils.ts) is more precise for auto: it only prefers the
  // resolved model's default_effort when Auto resolved to an explicit/project
  // model (`effectiveModel.id !== "auto"`); for GENERIC Auto
  // (`effectiveModel.id === "auto"`) it uses the classifyAutoEffort
  // complexity heuristic instead, since the "auto" catalog entry's own
  // default_effort just describes whatever reasoning model Auto happens to
  // route to server-side, not a deliberate user/project choice (#2893).
  const reasoningEffort: ReasoningEffortOverride =
    effortOverride ??
    ((effectiveModel?.default_effort as ReasoningEffortOverride | undefined) ||
      MODE_TO_EFFORT[reasoningMode])

  return {
    reasoningMode,
    handleReasoningModeChange,
    modelOverride,
    setModelOverride: handleModelOverrideChange,
    effortOverride,
    setEffortOverride: handleEffortOverrideChange,
    enableHighReasoning,
    showImageReasoningWarning,
    isReasoningModelActive,
    handleImageReasoningWarningClose,
    handleImageReasoningWarningCancel,
    handleHighReasoningChange,
    getEffectiveModelId,
    reasoningEffort,
    effectiveModel,
    /** Session models, with the route-default overlay substituted or appended for that id. */
    selectableModels,
    reasoningPresets: presets,
  }
}
